import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ReactNode } from 'react'
import { RiArrowRightLine } from '@/shared/icons/remix'
import brandStarbucks from '../assets/welcome/brand-starbucks.svg'
import brandTarget from '../assets/welcome/brand-target.svg'
import brandWalmart from '../assets/welcome/brand-walmart.svg'
import iconSuccessCheck from '../assets/welcome/icon-success-check.svg'
import {
  welcomeBrandCaptionSx,
  welcomeBrandCardSelectedSx,
  welcomeBrandCardSx,
  welcomeBrandGridSx,
  welcomeBrandLogoSx,
  welcomeBrandTileSx,
  welcomeHowArrowSx,
  welcomeHowColSx,
  welcomeHowGridSx,
  welcomeHowSectionSx,
  welcomeHowSubtitleSx,
  welcomeHowTitleSx,
  welcomeStepBadgeSx,
  welcomeStepCardSx,
  welcomeStepHeaderSx,
  welcomeStepTitleSx,
} from '../styles/signupSurveyWelcomeStyles'

function mergeSx(base: SxProps<Theme>, extra?: SxProps<Theme> | null): SxProps<Theme> {
  return {
    ...(base as object),
    ...(extra ? (extra as object) : {}),
  } as SxProps<Theme>
}

const OPTIONS = ['Very often', 'Often', 'Sometimes', 'Rarely', 'Never'] as const

const BRANDS = [
  {
    name: 'Amazon',
    logoBg: '#111111',
    selected: true,
  },
  {
    name: 'Target',
    logoBg: '#CC0000',
    selected: false,
  },
  {
    name: 'Starbucks',
    logoBg: '#00704A',
    selected: false,
  },
  {
    name: 'Walmart',
    logoBg: '#0071CE',
    selected: false,
  },
] as const

function AmazonLogo({ compact }: { readonly compact?: boolean }) {
  const fontSize = compact ? 16 : 22
  const smileWidth = compact ? 64 : 88
  return (
    <Box sx={{ textAlign: 'center', px: 0.5, lineHeight: 0 }}>
      <Typography
        sx={{
          color: '#FFFFFF',
          fontWeight: 800,
          fontSize,
          letterSpacing: '-0.4px',
          lineHeight: 1,
          fontFamily: 'Arial, Helvetica, sans-serif',
        }}
      >
        amazon
      </Typography>
      <Box
        component="svg"
        viewBox="0 0 72 12"
        sx={{
          width: smileWidth,
          height: compact ? 10 : 14,
          display: 'block',
          mx: 'auto',
          mt: compact ? 0.25 : 0.5,
        }}
        aria-hidden
      >
        <path
          d="M8 3c16 10 40 10 56 0"
          stroke="#FF9900"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
        />
        <path d="M62 3l8 5-7-1" fill="#FF9900" />
      </Box>
    </Box>
  )
}

function StepHeader({ n, title }: { readonly n: number; readonly title: string }) {
  return (
    <Box sx={welcomeStepHeaderSx}>
      <Box sx={welcomeStepBadgeSx}>{n}</Box>
      <Typography sx={welcomeStepTitleSx}>{title}</Typography>
    </Box>
  )
}

function SurveyOption({ option, index }: { readonly option: string; readonly index: number }) {
  const selected = index === 0
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: '10px', md: 1.25 },
        minHeight: { xs: 44, md: 40 },
        px: { xs: 1.5, md: 0 },
        py: { xs: 1, md: 0.75 },
        mt: { xs: index === 0 ? 0 : '7px', md: 0 },
        borderRadius: { xs: '10px', md: 0 },
        border: {
          xs: selected ? '1.1px solid #9DBDF3' : '1.1px solid #E1E7EF',
          md: 'none',
        },
        borderTop: {
          xs: selected ? '1.1px solid #9DBDF3' : '1.1px solid #E1E7EF',
          md: index === 0 ? 'none' : '1px solid #F1F5F9',
        },
        bgcolor: { xs: selected ? '#E8F0FD' : 'transparent', md: 'transparent' },
        fontSize: 13,
        fontWeight: selected ? 700 : 500,
        color: selected ? '#0F1B33' : '#475569',
        lineHeight: '19.5px',
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: selected ? '4.4px solid #2F6FE4' : '1.1px solid #94A3B8',
          bgcolor: '#FFFFFF',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      />
      {option}
    </Box>
  )
}

function BrandTile({
  name,
  logoBg,
  selected,
  children,
}: {
  readonly name: string
  readonly logoBg: string
  readonly selected: boolean
  readonly children: ReactNode
}) {
  return (
    <Box sx={mergeSx(welcomeBrandCardSx, selected ? welcomeBrandCardSelectedSx : null)}>
      <Box sx={mergeSx(welcomeBrandTileSx, { bgcolor: logoBg })}>{children}</Box>
      <Typography sx={welcomeBrandCaptionSx}>{name} gift card</Typography>
    </Box>
  )
}

export function SignupSurveyWelcomeHowItWorks() {
  return (
    <Box sx={welcomeHowSectionSx} data-testid="signup-survey-welcome-how">
      <Typography component="h2" sx={welcomeHowTitleSx}>
        How it works
      </Typography>
      <Typography component="p" sx={welcomeHowSubtitleSx}>
        Finish the survey, choose your reward, and get your gift card.
      </Typography>
      <Box sx={welcomeHowGridSx}>
        <Box sx={welcomeHowColSx}>
          <StepHeader n={1} title="Complete the survey" />
          <Box sx={welcomeStepCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  flex: 1,
                  height: 5,
                  borderRadius: '99px',
                  bgcolor: '#E6EBF2',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ width: '16.7%', height: '100%', bgcolor: '#2F6FE4', borderRadius: '99px' }} />
              </Box>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: '#5B6B82', whiteSpace: 'nowrap' }}>
                2 of 12
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5, color: '#0F1B33', lineHeight: '22.5px' }}>
              How often do you shop online?
            </Typography>
            {OPTIONS.map((option, index) => (
              <SurveyOption key={option} option={option} index={index} />
            ))}
          </Box>
        </Box>

        <Box sx={welcomeHowArrowSx} aria-hidden>
          <RiArrowRightLine size={22} />
        </Box>

        <Box sx={welcomeHowColSx}>
          <StepHeader n={2} title="Choose your reward" />
          <Box sx={mergeSx(welcomeStepCardSx, { textAlign: 'center', alignItems: 'center', justifyContent: 'center' })}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.75, color: '#0F1B33', lineHeight: '22.5px' }}>
              Choose your gift card
            </Typography>
            <Box sx={welcomeBrandGridSx}>
              {BRANDS.map((brand) => (
                <BrandTile key={brand.name} {...brand}>
                  {brand.name === 'Amazon' ? <AmazonLogo compact /> : null}
                  {brand.name === 'Target' ? (
                    <Box component="img" src={brandTarget} alt="" sx={welcomeBrandLogoSx} />
                  ) : null}
                  {brand.name === 'Starbucks' ? (
                    <Box component="img" src={brandStarbucks} alt="" sx={welcomeBrandLogoSx} />
                  ) : null}
                  {brand.name === 'Walmart' ? (
                    <Box component="img" src={brandWalmart} alt="" sx={welcomeBrandLogoSx} />
                  ) : null}
                </BrandTile>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={welcomeHowArrowSx} aria-hidden>
          <RiArrowRightLine size={22} />
        </Box>

        <Box sx={welcomeHowColSx}>
          <StepHeader n={3} title="Get your gift card" />
          <Box
            sx={mergeSx(welcomeStepCardSx, {
              textAlign: 'center',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
            })}
          >
            <Box>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '26px',
                  bgcolor: '#DCF5E6',
                  display: 'grid',
                  placeItems: 'center',
                  mx: 'auto',
                  mb: 1.25,
                  flexShrink: 0,
                }}
              >
                <Box component="img" src={iconSuccessCheck} alt="" />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#0F1B33', lineHeight: '25.5px' }}>
                You&apos;re all set!
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: '19.5px' }}>
                Here is your Amazon gift card.
              </Typography>
            </Box>
            <Box
              sx={{
                mx: 'auto',
                width: '100%',
                maxWidth: 200,
                height: 112,
                borderRadius: '12px',
                bgcolor: '#0F1B33',
                color: '#FFFFFF',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                boxShadow: { xs: '0px 12px 24px rgba(15, 27, 51, 0.18)', md: 'none' },
              }}
            >
              <AmazonLogo />
              <Typography
                sx={{
                  position: 'absolute',
                  left: 14,
                  bottom: 12,
                  fontSize: 7,
                  fontWeight: 700,
                  letterSpacing: '1.1px',
                  color: 'rgba(255,255,255,0.62)',
                }}
              >
                DIGITAL GIFT CARD
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: '#E8F0FD',
                minHeight: 44,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                color: '#1D5FD1',
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
                width: '100%',
              }}
            >
              View Gift Card
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
